import { useMemo } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    useLoaderData,
    useNavigation,
    useSearchParams,
} from '@remix-run/react';
import { useTracker } from '@archipro-website/tracker';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { PermissionEnum } from 'generated/graphql';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { useEditPermissions, usePermissions } from '~/modules/user';
import AboutProfessional from '@modules/project/component/about-professional/AboutProfessional';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import { CarouselItem, type CarouselOptions } from '@rocco/components/carousel';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { CarouselSection } from '~/ui-rocco/components/carousel-section/CarouselSection';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';

export const SHOW_ALL_PARAM_FEATURED_PRODUCTS = 'featured-products-show-all';
export const SHOW_ALL_PARAM_FEATURED_PROFESSIONALS =
    'featured-professionals-show-all';

const ProjectPageContent = () => {
    const { desktop } = useAppDisplayModeContext();
    const {
        project,
        featuredProducts,
        featuredProfessionals,
        featuredProjects,
        popularProjects,
        recentlyViewedProjects,
    } = useLoaderData<typeof loader>();
    const { handleFollow, isInDesignBoard } = useHandleFollow();
    const tracker = useTracker();

    // Handler for tracking clicks on popular projects in "Other Projects you might like" section
    const handleOtherProjectLikeProjectClick = (libraryItemID: number) => {
        tracker.log('OtherProjectLikeProjectClick', {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: libraryItemID,
            },
        });
    };

    // Handler for tracking clicks on recently viewed projects
    const handleRecentlyViewedProjectClick = (libraryItemID: number) => {
        tracker.log('RecentlyViewedProjectClick', {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: libraryItemID,
            },
        });
    };

    // Deduplicate popular projects to exclude the current project and all projects by this professional
    const deduplicatedPopularProjects = useMemo(() => {
        return popularProjects.projects.filter(
            (popularProject) =>
                popularProject.professional.id !== project.Professional.ID
        );
    }, [popularProjects.projects, project.Professional.ID]);

    const carouselOptionsProjects: CarouselOptions = useMemo(
        () => ({
            slidesToScroll: 4,
            align: 'start',
            watchDrag: (api, evt) => {
                return (
                    evt.target instanceof Element &&
                    !evt.target.closest('.slider-item-image')
                );
            },
        }),
        []
    );

    const carouselOptionsProducts: CarouselOptions = useMemo(
        () => ({
            slidesToScroll: 5,
            align: 'start',
            watchDrag: (api, evt) => {
                return (
                    evt.target instanceof Element &&
                    !evt.target.closest('.slider-item-image')
                );
            },
        }),
        []
    );

    const navigation = useNavigation();
    const [searchParams] = useSearchParams();
    const { canEditProfile } = useEditPermissions(project.Professional.ID);
    const canCopyLink = usePermissions(PermissionEnum.BmMarketingView);
    const projectEditPath = getBMPagePath(
        project.Professional.URLSegment,
        'projects',
        project.URLSegment
    );

    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const showingAllFeaturedProducts = !!searchParams.get(
        SHOW_ALL_PARAM_FEATURED_PRODUCTS
    );
    const showingAllFeaturedProfessionals = !!searchParams.get(
        SHOW_ALL_PARAM_FEATURED_PROFESSIONALS
    );
    const featuredShowAllFeaturedProducts =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PRODUCTS) === '1';
    const featuredShowAllFeaturedProfessionals =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PROFESSIONALS) === '1';

    const isLoadingFeaturedProducts =
        navigation.state === 'loading' && featuredShowAllFeaturedProducts;
    const isLoadingFeaturedProfessionals =
        navigation.state === 'loading' && featuredShowAllFeaturedProfessionals;

    const featuredProductsList: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            featuredProducts.FeaturedProducts.products.map((product) => ({
                model: product,
                aspectRatio: 'square',
                section: 'ProjectUsedProducts',
            })),
        [featuredProducts.FeaturedProducts.products]
    );
    const featuredProfessionalsList: TileViewModel<ProfessionalTileViewModel>[] =
        useMemo(
            () =>
                featuredProfessionals.FeaturedProfessionals.professionals.map(
                    (professional) => ({
                        model: professional,
                        aspectRatio: 'default',
                        section: 'ProjectUsedProfessionals',
                    })
                ),
            [featuredProfessionals.FeaturedProfessionals.professionals]
        );

    const featuredProjectsList: TileViewModel<ProjectTileViewModel>[] = useMemo(
        () =>
            featuredProjects.FeaturedProjects.projects.map((project) => ({
                model: project,
                aspectRatio: 'default',
                section: 'ProjectUsedProjects',
            })),
        [featuredProjects.FeaturedProjects.projects]
    );
    const popularProjectsList: TileViewModel<ProjectTileViewModel>[] = useMemo(
        () =>
            deduplicatedPopularProjects.map((project) => ({
                model: project,
                aspectRatio: 'default',
                section: 'PopularProjects',
            })),
        [deduplicatedPopularProjects]
    );
    const featuredProductsAdmin = useTilesAdminData(featuredProductsList);
    const featuredProfessionalsAdmin = useTilesAdminData(
        featuredProfessionalsList
    );
    const popularProjectsAdmin = useTilesAdminData(popularProjectsList);
    const featuredProjectsAdmin = useTilesAdminData(featuredProjectsList);

    const recentlyViewedProjectsList: TileViewModel<ProjectTileViewModel>[] =
        useMemo(
            () =>
                recentlyViewedProjects
                    ?.filter((recentProject) => recentProject.ID !== project.ID) // Exclude current project
                    .map((recentProject) => ({
                        model: mapProjectToTileViewModel(recentProject, {
                            appDisplayMode: desktop ? 'desktop' : 'mobile',
                        }),
                        aspectRatio: 'default',
                        section: 'RecentlyViewedProjects',
                    })) ?? [],
            [recentlyViewedProjects, project.ID, desktop]
        );

    const recentlyViewedProjectsAdmin = useTilesAdminData(
        recentlyViewedProjectsList
    );

    return (
        <div className="flex flex-col gap-y-12 px-4 md:gap-y-8 md:px-0">
            {featuredProducts.FeaturedProducts.pageInfo.totalCount > 0 &&
                desktop && (
                    <CarouselSection
                        title="Products used in this Project"
                        options={carouselOptionsProducts}
                        className="py-20 md:pl-12"
                    >
                        {featuredProductsAdmin.map((tile) => (
                            <CarouselItem
                                key={`featured-product-${tile.model.id}`}
                                className="max-w-30 first:ml-0 md:max-w-87"
                            >
                                <Tile
                                    tile={{
                                        ...tile,
                                        isFollowed: isInDesignBoard(
                                            tile.model.id,
                                            'ProductPin'
                                        ),
                                        onFollow: (pinId, pinType) =>
                                            handleFollow(
                                                { id: pinId },
                                                pinType
                                            ),
                                    }}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselSection>
                )}

            {featuredProfessionalsAdmin.length > 0 && desktop && (
                <CarouselSection
                    title={`Professionals used in ${project.Title}`}
                    className="py-20 md:pl-12"
                    options={carouselOptionsProjects}
                >
                    {featuredProfessionalsAdmin.map((tile) => (
                        <CarouselItem
                            key={`featured-professional-${tile.model.id}`}
                            className="max-w-30 first:ml-0 md:max-w-110"
                        >
                            <Tile
                                tile={{
                                    ...tile,
                                    isFollowed: isInDesignBoard(
                                        tile.model.id,
                                        'ProfessionalPin'
                                    ),
                                    onFollow: (pinId, pinType) =>
                                        handleFollow({ id: pinId }, pinType),
                                }}
                            />
                        </CarouselItem>
                    ))}
                </CarouselSection>
            )}
            <section className="md:pl-8">
                {/* Featured Products */}
                {!desktop && (
                    <DetailsPageRelatedTileGrid
                        type="products"
                        title={`Products used in ${project.Title}`}
                        tiles={featuredProductsAdmin}
                        cta={
                            showingAllFeaturedProducts ||
                            featuredProducts.FeaturedProducts.pageInfo
                                .totalCount <= (desktop ? 5 : 4)
                                ? undefined
                                : {
                                      type: 'link',
                                      to: `?${SHOW_ALL_PARAM_FEATURED_PRODUCTS}=1`,
                                      label: 'View all',
                                      loading: isLoadingFeaturedProducts
                                          ? true
                                          : undefined,
                                      preventScrollReset: true,
                                  }
                        }
                        displayCount={{
                            desktop: 10,
                            mobile: 4,
                        }}
                        appDisplayMode={desktop ? 'desktop' : 'mobile'}
                        noSlice={showingAllFeaturedProducts}
                    />
                )}
                {/* Featured Professionals */}
                {!desktop && (
                    <DetailsPageRelatedTileGrid
                        type="professionals"
                        title={`Professionals used in ${project.Title}`}
                        tiles={featuredProfessionalsAdmin}
                        appDisplayMode={desktop ? 'desktop' : 'mobile'}
                        cta={
                            showingAllFeaturedProfessionals ||
                            featuredProfessionals.FeaturedProfessionals.pageInfo
                                .totalCount <= 4
                                ? undefined
                                : {
                                      type: 'link',
                                      to: `?${SHOW_ALL_PARAM_FEATURED_PROFESSIONALS}=1`,
                                      label: 'View all',
                                      loading: isLoadingFeaturedProfessionals
                                          ? true
                                          : undefined,
                                      preventScrollReset: true,
                                  }
                        }
                        displayCount={{
                            desktop: 4,
                            mobile: 4,
                        }}
                        noSlice={showingAllFeaturedProfessionals}
                    />
                )}
            </section>

            {desktop && (
                <section className="md:px-7">
                    <AboutProfessional
                        data={project}
                        tiles={featuredProjectsAdmin}
                    />
                </section>
            )}

            {/* Featured Projects */}
            {featuredProjectsAdmin.length > 0 && !desktop && (
                <DetailsPageRelatedTileGrid
                    type="projects"
                    title={`More projects by ${project.Professional.Title}`}
                    tiles={featuredProjectsAdmin}
                    appDisplayMode={desktop ? 'desktop' : 'mobile'}
                    cta={
                        featuredProjects.FeaturedProjects.pageInfo.totalCount <=
                        4
                            ? undefined
                            : {
                                  type: 'link',
                                  to: `${project.Professional.Link}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                  label: 'View more',
                              }
                    }
                    displayCount={{
                        desktop: 4,
                        mobile: 4,
                    }}
                />
            )}

            {/* Popular Projects Carousel*/}
            {deduplicatedPopularProjects.length > 0 && desktop && (
                <CarouselSection
                    title="Other Projects you might like"
                    options={carouselOptionsProjects}
                    className="py-20"
                >
                    {popularProjectsAdmin.map((tile) => (
                        <CarouselItem
                            key={`popular-project-${tile.model.id}`}
                            className="max-w-30 first:ml-0 md:max-w-110"
                        >
                            <div
                                onClick={() =>
                                    handleOtherProjectLikeProjectClick(
                                        tile.model.id
                                    )
                                }
                            >
                                <Tile
                                    tile={{
                                        ...tile,
                                        isFollowed: isInDesignBoard(
                                            tile.model.id,
                                            'ProjectPin'
                                        ),
                                        onFollow: (pinId, pinType) =>
                                            handleFollow(
                                                { id: pinId },
                                                pinType
                                            ),
                                    }}
                                />
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselSection>
            )}

            {/* Recently Viewed Projects */}
            {recentlyViewedProjectsList.length > 0 && desktop && (
                <CarouselSection
                    title="Recently viewed"
                    options={carouselOptionsProjects}
                    className="py-20"
                >
                    {recentlyViewedProjectsAdmin.map((tile) => (
                        <CarouselItem
                            key={`recently-viewed-project-${tile.model.id}`}
                            className="max-w-30 first:ml-0 md:max-w-110"
                        >
                            <div
                                onClick={() =>
                                    handleRecentlyViewedProjectClick(
                                        tile.model.id
                                    )
                                }
                            >
                                <Tile
                                    tile={{
                                        ...tile,
                                        isFollowed: isInDesignBoard(
                                            tile.model.id,
                                            'ProjectPin'
                                        ),
                                        onFollow: (pinId, pinType) =>
                                            handleFollow(
                                                { id: pinId },
                                                pinType
                                            ),
                                    }}
                                />
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselSection>
            )}

            {desktop && (
                <div className="md:px-12">
                    <DetailPageButtons
                        id={project.ID}
                        editLink={projectEditPath}
                        shareableLink={project.Link}
                        canEdit={canEditProfile}
                        canCopyLink={canCopyLink}
                    />
                </div>
            )}
        </div>
    );
};
export default ProjectPageContent;
